import {
  ApplicationRef,
  inject,
  Injectable,
  InjectionToken,
} from '@angular/core';
import { AsyncSubject, Observable, take, zip } from 'rxjs';

import {
  LoginProvider,
  SocialUser,
} from '@personal-area/global/social-authorization/models';

/**
 * An interface to define the shape of the service configuration options.
 */
export interface SocialAuthServiceConfig {
  autoLogin?: boolean;
  providers: { id: string; provider: LoginProvider }[];
  onError?: (error: any) => any;
}

export const SOCIAL_AUTH_SERVICE_CONFIG =
  new InjectionToken<SocialAuthServiceConfig>('SocialAuthServiceConfig');

/**
 * Service for CDK-based social authentication (Facebook, Google login providers).
 * Provides `signIn` method for client-side SDK authentication.
 *
 * Note: This service is optional and requires SocialAuthServiceConfig provider.
 * Apps without CDK configuration will skip initialization gracefully.
 *
 * @dynamic
 */
@Injectable({ providedIn: 'root' })
export class SocialAuthService {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  static readonly ERR_LOGIN_PROVIDER_NOT_FOUND = 'Login provider not found';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  static readonly ERR_NOT_INITIALIZED =
    'Login providers not ready yet. Are there errors on your console?';

  private readonly _config = inject<
    SocialAuthServiceConfig | Promise<SocialAuthServiceConfig> | null
  >(SOCIAL_AUTH_SERVICE_CONFIG, { optional: true });
  private readonly _appRef = inject(ApplicationRef);

  private _providers: Map<string, LoginProvider> = new Map();

  /* Consider making this an enum comprising LOADING, LOADED, FAILED etc. */
  private _initialized = false;
  private _initState: AsyncSubject<boolean> = new AsyncSubject();

  constructor() {
    this._initializeConfig();
  }

  private _initializeConfig(): void {
    if (this._config) {
      if (this._config instanceof Promise) {
        this._config.then((config) => {
          this._initialize(config);
        });
      } else {
        this._initialize(this._config);
      }
    }
  }

  private _initialize(config: SocialAuthServiceConfig): void {
    const { onError = console.error } = config;

    config.providers.forEach((item) => {
      this._providers.set(item.id, item.provider);
    });

    zip(
      ...Array.from(this._providers.values()).map((provider) =>
        provider.initialize(),
      ),
    )
      .pipe(take(1))
      .subscribe({
        error: (error) => {
          this._initialized = true;
          this._initState.next(this._initialized);
          this._initState.complete();
          onError(error);
        },
        complete: () => {
          this._initialized = true;
          this._initState.next(this._initialized);
          this._initState.complete();
        },
      });
  }

  /**
   * A method used to sign in a user with a specific `LoginProvider`.
   *
   * @param providerId Id with which the `LoginProvider` has been registered with the service
   * @param signInOptions Optional `LoginProvider` specific arguments
   * @returns A `Observable` that resolves to the authenticated user information
   */
  public signIn(
    providerId: string,
    signInOptions?: unknown,
  ): Observable<SocialUser> {
    return new Observable<SocialUser>((observer) => {
      if (!this._initialized) {
        observer.error(SocialAuthService.ERR_NOT_INITIALIZED);
      } else {
        const providerObject = this._providers.get(providerId);
        if (providerObject) {
          providerObject.signIn(signInOptions).subscribe({
            next: (user: SocialUser) => {
              observer.next({ ...user, provider: providerId });
              observer.complete();

              // Say to angular that stream is done, cause sdk code is envoke outside zone
              this._appRef.tick();
            },
            error: (error) => {
              observer.error(error);
            },
          });
        } else {
          observer.error(SocialAuthService.ERR_LOGIN_PROVIDER_NOT_FOUND);
        }
      }
    });
  }
}
